import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Car, Users, FileText, BarChart3, Plus, Search, X,
  CheckCircle2, AlertCircle, Clock, DollarSign, TrendingUp, ExternalLink, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";

const STATUS_COLORS = {
  available: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  leased: "bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/30",
  rented: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  maintenance: "bg-orange-500/15 text-orange-400 border-orange-500/30",
  sold: "bg-white/10 text-white/50 border-white/20",
  active: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  draft: "bg-white/10 text-white/50 border-white/20",
  completed: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  terminated: "bg-red-500/15 text-red-400 border-red-500/30",
  paid: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  overdue: "bg-red-500/15 text-red-400 border-red-500/30",
  due: "bg-white/10 text-white/50 border-white/20",
  waived: "bg-white/5 text-white/40 border-white/10",
};

const fmtCurrency = (n) =>
  n == null ? "—" : new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 0 }).format(n);

export default function AdminFleet() {
  const [tab, setTab] = useState("fleet"); // fleet | leases | analytics

  return (
    <div data-testid="admin-fleet-page" className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/10 bg-[#080808] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-8 h-16 flex items-center justify-between">
          <Logo />
          <Link
            to="/admin"
            data-testid="admin-fleet-back"
            className="inline-flex items-center gap-2 text-xs text-white/60 hover:text-[#D4AF37] tracking-wide"
          >
            <ArrowLeft size={14} /> Back to Dashboard
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-8 py-10">
        <div className="label-eyebrow mb-3">RTO · Fleet Management</div>
        <h1 className="font-display text-4xl md:text-5xl text-white mb-6" style={{ fontWeight: 300 }}>
          Fleet <span className="italic text-[#D4AF37]">Console</span>
        </h1>

        <div className="flex items-center gap-1 border-b border-white/10 mb-8">
          {[
            { id: "fleet", label: "Fleet", icon: <Car size={14} /> },
            { id: "leases", label: "Leases", icon: <FileText size={14} /> },
            { id: "analytics", label: "Analytics", icon: <BarChart3 size={14} /> },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-fleet-tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-5 py-3 text-sm tracking-wide border-b-2 -mb-px transition-colors ${
                tab === t.id
                  ? "text-[#D4AF37] border-[#D4AF37]"
                  : "text-white/50 border-transparent hover:text-white"
              }`}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {tab === "fleet" && <FleetTab />}
        {tab === "leases" && <LeasesTab />}
        {tab === "analytics" && <AnalyticsTab />}
      </main>
    </div>
  );
}

// ==================== FLEET TAB ====================

function FleetTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (q) params.q = q;
      const { data } = await api.get("/admin/rto/fleet", { params });
      setRows(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Failed to load fleet"));
    } finally {
      setLoading(false);
    }
  }, [q, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!confirm("Delete this vehicle from fleet? (Only allowed if no active lease.)")) return;
    try {
      await api.delete(`/admin/rto/fleet/${id}`);
      toast.success("Vehicle deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Delete failed"));
    }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-6 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input
            data-testid="fleet-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search VIN, rego, make, model…"
            className="w-full bg-[#0a0a0a] border border-white/10 text-white pl-10 pr-4 py-2.5 text-sm focus:border-[#D4AF37] focus:outline-none"
          />
        </div>
        <select
          data-testid="fleet-status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-[#0a0a0a] border border-white/10 text-white px-4 py-2.5 text-sm focus:border-[#D4AF37] focus:outline-none"
        >
          <option value="all">All statuses</option>
          <option value="available">Available</option>
          <option value="leased">Leased</option>
          <option value="rented">Rented</option>
          <option value="maintenance">Maintenance</option>
          <option value="sold">Sold</option>
        </select>
        <button
          onClick={() => setShowModal(true)}
          data-testid="fleet-add-btn"
          className="inline-flex items-center gap-2 bg-[#D4AF37] text-black font-medium px-4 py-2.5 text-sm hover:bg-[#F6ECCC] transition-colors"
        >
          <Plus size={14} /> Add Vehicle
        </button>
      </div>

      <div className="bg-[#0a0a0a] border border-white/10">
        {loading ? (
          <div className="p-16 text-center text-white/50 text-sm">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-16 text-center text-white/50 text-sm" data-testid="fleet-empty">
            No vehicles yet. Click <b>Add Vehicle</b> to seed your first one.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="fleet-table">
            <thead className="text-xs uppercase tracking-[0.12em] text-white/50 border-b border-white/10">
              <tr>
                {["Vehicle", "Rego / VIN", "Km", "Price", "Status", "Next Service", ""].map((h) => (
                  <th key={h} className="text-left px-5 py-3 font-normal">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const today = new Date().toISOString().slice(0, 10);
                const overdueByDate = r.next_service_date && r.next_service_date <= today;
                const overdueByKm = r.next_service_km && r.mileage_km && r.mileage_km >= r.next_service_km;
                const isOverdue = overdueByDate || overdueByKm;
                return (
                <tr key={r.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`fleet-row-${r.id}`}>
                  <td className="px-5 py-3">
                    <div className="text-white">{r.year} {r.make} {r.model}</div>
                    <div className="text-xs text-white/40">{r.variant || "—"}</div>
                  </td>
                  <td className="px-5 py-3">
                    <div className="text-white/80">{r.rego}</div>
                    <div className="text-xs text-white/40 font-mono">{r.vin}</div>
                  </td>
                  <td className="px-5 py-3 text-white/70">{(r.mileage_km || 0).toLocaleString()} km</td>
                  <td className="px-5 py-3 text-white/70">{fmtCurrency(r.drive_away_price)}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-block text-[10px] uppercase tracking-[0.16em] px-2 py-1 border ${STATUS_COLORS[r.status] || ""}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    {r.next_service_date || r.next_service_km ? (
                      <div className={`text-xs ${isOverdue ? "text-red-300" : "text-white/70"}`}>
                        {r.next_service_date || ""}{r.next_service_date && r.next_service_km ? " · " : ""}
                        {r.next_service_km ? `${r.next_service_km.toLocaleString()}km` : ""}
                        {isOverdue && <div className="text-[10px] uppercase tracking-widest">Overdue</div>}
                      </div>
                    ) : (
                      <span className="text-xs text-white/30">—</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex items-center gap-3">
                      <Link
                        to={`/admin/maintenance?vehicle_id=${r.id}`}
                        data-testid={`fleet-maintenance-${r.id}`}
                        className="text-xs text-[#D4AF37]/70 hover:text-[#D4AF37]"
                        title="Maintenance log"
                      >Log →</Link>
                      <button
                        onClick={() => remove(r.id)}
                        data-testid={`fleet-delete-${r.id}`}
                        className="text-white/40 hover:text-red-400 p-1"
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        )}
      </div>

      {showModal && <FleetModal onClose={() => setShowModal(false)} onSaved={() => { setShowModal(false); load(); }} />}
    </div>
  );
}

function FleetModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    vin: "", rego: "", make: "", model: "", variant: "", year: 2026,
    colour: "", mileage_km: 0, drive_away_price: 0, weekly_from: 0, status: "available",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/rto/fleet", { ...form, year: Number(form.year), mileage_km: Number(form.mileage_km), drive_away_price: Number(form.drive_away_price), weekly_from: Number(form.weekly_from) });
      toast.success("Vehicle added");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Add failed"));
    } finally {
      setSaving(false);
    }
  };

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Modal onClose={onClose} title="Add Vehicle" testId="fleet-modal">
      <form onSubmit={submit} className="space-y-4">
        <Row>
          <Field label="VIN *" value={form.vin} onChange={upd("vin")} testId="fleet-form-vin" required />
          <Field label="Rego *" value={form.rego} onChange={upd("rego")} testId="fleet-form-rego" required />
        </Row>
        <Row>
          <Field label="Make *" value={form.make} onChange={upd("make")} testId="fleet-form-make" required />
          <Field label="Model *" value={form.model} onChange={upd("model")} testId="fleet-form-model" required />
        </Row>
        <Row>
          <Field label="Year *" value={form.year} onChange={upd("year")} type="number" testId="fleet-form-year" required />
          <Field label="Variant" value={form.variant} onChange={upd("variant")} testId="fleet-form-variant" />
        </Row>
        <Row>
          <Field label="Colour" value={form.colour} onChange={upd("colour")} testId="fleet-form-colour" />
          <Field label="Mileage (km)" value={form.mileage_km} onChange={upd("mileage_km")} type="number" testId="fleet-form-mileage" />
        </Row>
        <Row>
          <Field label="Drive-away Price ($) *" value={form.drive_away_price} onChange={upd("drive_away_price")} type="number" step="1" testId="fleet-form-price" required />
          <Field label="Weekly from ($)" value={form.weekly_from} onChange={upd("weekly_from")} type="number" step="1" testId="fleet-form-weekly" />
        </Row>
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.22em] text-white/50 mb-2 block">Status</span>
          <select
            value={form.status}
            onChange={upd("status")}
            data-testid="fleet-form-status"
            className="w-full bg-[#050505] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#D4AF37] focus:outline-none"
          >
            <option value="available">Available</option>
            <option value="maintenance">Maintenance</option>
            <option value="sold">Sold</option>
          </select>
        </label>
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose} className="flex-1 border border-white/15 text-white py-3 hover:border-white/30">Cancel</button>
          <button type="submit" disabled={saving} data-testid="fleet-form-submit" className="flex-1 bg-[#D4AF37] text-black font-medium py-3 hover:bg-[#F6ECCC] disabled:opacity-40">
            {saving ? "Adding…" : "Add Vehicle"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ==================== LEASES TAB ====================

function LeasesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/rto/leases");
      setRows(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Failed to load leases"));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-6 items-center justify-between">
        <p className="text-sm text-white/50">All lease contracts with their payment schedules.</p>
        <button
          onClick={() => setShowModal(true)}
          data-testid="lease-new-btn"
          className="inline-flex items-center gap-2 bg-[#D4AF37] text-black font-medium px-4 py-2.5 text-sm hover:bg-[#F6ECCC] transition-colors"
        >
          <Plus size={14} /> New Lease
        </button>
      </div>

      <div className="bg-[#0a0a0a] border border-white/10">
        {loading ? (
          <div className="p-16 text-center text-white/50 text-sm">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-16 text-center text-white/50 text-sm" data-testid="leases-empty">
            No leases yet. Click <b>New Lease</b> to bind a vehicle to a customer.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="leases-table">
            <thead className="text-xs uppercase tracking-[0.12em] text-white/50 border-b border-white/10">
              <tr>
                {["Contract", "Customer", "Vehicle", "Term", "Payment", "TCV", "Status", ""].map((h) => (
                  <th key={h} className="text-left px-4 py-3 font-normal">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-white/5 hover:bg-white/5 cursor-pointer" onClick={() => setSelected(r.id)} data-testid={`lease-row-${r.id}`}>
                  <td className="px-4 py-3 text-white/90 font-mono">{r.contract_number}</td>
                  <td className="px-4 py-3 text-white/80">{r.customer_name}</td>
                  <td className="px-4 py-3 text-white/70">{r.vehicle_label} <span className="text-white/40">· {r.vehicle_rego}</span></td>
                  <td className="px-4 py-3 text-white/70">{r.term_weeks}w</td>
                  <td className="px-4 py-3 text-white/80">{fmtCurrency(r.payment_amount)}/{r.payment_frequency[0]}</td>
                  <td className="px-4 py-3 text-white/70">{fmtCurrency(r.total_contract_value)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block text-[10px] uppercase tracking-[0.16em] px-2 py-1 border ${STATUS_COLORS[r.status] || ""}`}>{r.status}</span>
                  </td>
                  <td className="px-4 py-3 text-right text-white/40">→</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && <LeaseModal onClose={() => setShowModal(false)} onSaved={() => { setShowModal(false); load(); }} />}
      {selected && <LeaseDrawer leaseId={selected} onClose={() => { setSelected(null); load(); }} />}
    </div>
  );
}

function LeaseModal({ onClose, onSaved }) {
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [form, setForm] = useState({
    type: "rent_to_own", customer_id: "", vehicle_id: "",
    start_date: new Date().toISOString().slice(0, 10),
    term_weeks: 156, payment_frequency: "weekly", payment_amount: 254, deposit_amount: 0,
  });
  const [autoCalc, setAutoCalc] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/rto/customers").then((r) => setCustomers(r.data || []));
    api.get("/admin/rto/fleet", { params: { status: "available" } }).then((r) => setVehicles(r.data || []));
  }, []);

  useEffect(() => {
    if (!autoCalc || !form.vehicle_id) return;
    const veh = vehicles.find((v) => v.id === form.vehicle_id);
    if (!veh) return;
    const financed = Math.max(0, veh.drive_away_price - form.deposit_amount);
    const years = Math.max(1, Math.floor(form.term_weeks / 52));
    const total = financed * (1 + 0.135 * years);
    let n = form.term_weeks;
    if (form.payment_frequency === "fortnightly") n = Math.max(1, Math.floor(form.term_weeks / 2));
    else if (form.payment_frequency === "monthly") n = Math.max(1, Math.round(form.term_weeks / 4.345));
    setForm((f) => ({ ...f, payment_amount: Math.round((total / n) * 100) / 100 }));
  }, [autoCalc, form.vehicle_id, form.term_weeks, form.payment_frequency, form.deposit_amount, vehicles]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_id || !form.vehicle_id) return toast.error("Pick a customer and vehicle");
    setSaving(true);
    try {
      await api.post("/admin/rto/leases", {
        ...form,
        term_weeks: Number(form.term_weeks),
        payment_amount: Number(form.payment_amount),
        deposit_amount: Number(form.deposit_amount),
      });
      toast.success("Lease created — payment schedule generated");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Create failed"));
    } finally { setSaving(false); }
  };
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Modal onClose={onClose} title="New Lease" testId="lease-modal">
      <form onSubmit={submit} className="space-y-4">
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.22em] text-white/50 mb-2 block">Type</span>
          <select value={form.type} onChange={upd("type")} data-testid="lease-form-type" className="w-full bg-[#050505] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#D4AF37] focus:outline-none">
            <option value="rent_to_own">Rent-to-Own</option>
            <option value="rental">Short-term Rental</option>
          </select>
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.22em] text-white/50 mb-2 block">Customer *</span>
          <select value={form.customer_id} onChange={upd("customer_id")} data-testid="lease-form-customer" required className="w-full bg-[#050505] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#D4AF37] focus:outline-none">
            <option value="">Pick a customer…</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.full_name} · {c.email}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.22em] text-white/50 mb-2 block">Vehicle *  <span className="text-white/30 normal-case tracking-normal">(available only)</span></span>
          <select value={form.vehicle_id} onChange={upd("vehicle_id")} data-testid="lease-form-vehicle" required className="w-full bg-[#050505] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#D4AF37] focus:outline-none">
            <option value="">Pick a vehicle…</option>
            {vehicles.map((v) => <option key={v.id} value={v.id}>{v.year} {v.make} {v.model} · {v.rego} · {fmtCurrency(v.drive_away_price)}</option>)}
          </select>
        </label>
        <Row>
          <Field label="Start date *" type="text" placeholder="YYYY-MM-DD" value={form.start_date} onChange={upd("start_date")} testId="lease-form-start" required />
          <Field label="Term (weeks) *" type="number" value={form.term_weeks} onChange={upd("term_weeks")} testId="lease-form-term" required />
        </Row>
        <Row>
          <label className="block flex-1">
            <span className="text-[10px] uppercase tracking-[0.22em] text-white/50 mb-2 block">Frequency</span>
            <select value={form.payment_frequency} onChange={upd("payment_frequency")} data-testid="lease-form-freq" className="w-full bg-[#050505] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#D4AF37] focus:outline-none">
              <option value="weekly">Weekly</option>
              <option value="fortnightly">Fortnightly</option>
              <option value="monthly">Monthly</option>
            </select>
          </label>
          <Field label="Deposit ($)" type="number" step="1" value={form.deposit_amount} onChange={upd("deposit_amount")} testId="lease-form-deposit" />
        </Row>
        <div className="border border-white/10 bg-[#050505] p-3">
          <label className="flex items-center gap-2 mb-3 cursor-pointer">
            <input type="checkbox" checked={autoCalc} onChange={(e) => setAutoCalc(e.target.checked)} data-testid="lease-form-autocalc" className="accent-[#D4AF37]" />
            <span className="text-xs text-white/70">Auto-calculate from 13.5% formula (uncheck to override)</span>
          </label>
          <Field label="Payment amount per instalment ($) *" type="number" step="0.01" value={form.payment_amount} onChange={(e) => { setAutoCalc(false); upd("payment_amount")(e); }} testId="lease-form-amount" required />
        </div>
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose} className="flex-1 border border-white/15 text-white py-3 hover:border-white/30">Cancel</button>
          <button type="submit" disabled={saving} data-testid="lease-form-submit" className="flex-1 bg-[#D4AF37] text-black font-medium py-3 hover:bg-[#F6ECCC] disabled:opacity-40">
            {saving ? "Creating…" : "Create Lease + Schedule"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function LeaseDrawer({ leaseId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/rto/leases/${leaseId}`);
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load lease");
    } finally { setLoading(false); }
  }, [leaseId]);
  useEffect(() => { load(); }, [load]);

  // Close on Escape key
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const [inviting, setInviting] = useState(false);
  const sendInvite = async () => {
    if (!data?.customer?.id) return;
    setInviting(true);
    try {
      const r = await api.post(`/admin/portal/customers/${data.customer.id}/invite`);
      if (r.data.email_failed) {
        toast.warning("Portal enabled — but email delivery failed. Copy link manually.");
      } else {
        toast.success(`Portal invite sent to ${data.customer.email}`);
      }
      if (r.data.invite_url) {
        await navigator.clipboard.writeText(r.data.invite_url).catch(() => {});
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Invite failed"));
    } finally { setInviting(false); }
  };

  const markPaid = async (pid) => {
    try {
      await api.post(`/admin/rto/payments/${pid}/mark-paid`, { method: "manual" });
      toast.success("Marked paid");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail, "Failed")); }
  };
  const stripeLink = async (pid) => {
    try {
      const r = await api.post(`/admin/rto/payments/${pid}/stripe-link`, {});
      await navigator.clipboard.writeText(r.data.checkout_url).catch(() => {});
      toast.success("Stripe link copied to clipboard");
      window.open(r.data.checkout_url, "_blank", "noopener,noreferrer");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail, "Stripe error")); }
  };
  const terminate = async () => {
    if (!confirm("Terminate this lease? The vehicle will be marked available.")) return;
    try {
      await api.post(`/admin/rto/leases/${leaseId}/terminate`);
      toast.success("Lease terminated");
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail, "Failed")); }
  };

  return (
    <div data-testid="lease-drawer" className="fixed inset-0 z-40 flex justify-end">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative w-full max-w-2xl h-full bg-[#080808] border-l border-white/10 overflow-y-auto">
        <div className="sticky top-0 bg-[#080808] border-b border-white/10 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/40">Lease</div>
            <div className="text-white font-mono">{data?.lease?.contract_number || "…"}</div>
          </div>
          <button onClick={onClose} data-testid="lease-drawer-close" className="text-white/50 hover:text-white p-1"><X size={18} /></button>
        </div>

        {loading || !data ? (
          <div className="p-16 text-center text-white/50 text-sm">Loading…</div>
        ) : (
          <div className="p-6 space-y-5">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <Info label="Customer" value={data.customer.full_name} sub={data.customer.email} />
              <Info label="Vehicle" value={`${data.vehicle.year} ${data.vehicle.make} ${data.vehicle.model}`} sub={data.vehicle.rego} />
              <Info label="Term" value={`${data.lease.term_weeks} weeks · ${data.lease.payment_frequency}`} />
              <Info label="Payment" value={`${fmtCurrency(data.lease.payment_amount)} / instalment`} />
              <Info label="Deposit" value={fmtCurrency(data.lease.deposit_amount)} />
              <Info label="TCV" value={fmtCurrency(data.lease.total_contract_value)} />
              <Info label="Start" value={data.lease.start_date} />
              <Info label="End" value={data.lease.end_date} />
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-3">Payment schedule ({data.payments.length})</div>
              <div className="border border-white/10 max-h-[50vh] overflow-y-auto">
                <table className="w-full text-sm" data-testid="lease-schedule-table">
                  <thead className="text-xs text-white/50 sticky top-0 bg-[#0a0a0a]">
                    <tr><th className="text-left px-3 py-2 font-normal">#</th><th className="text-left px-3 py-2 font-normal">Due</th><th className="text-left px-3 py-2 font-normal">Amount</th><th className="text-left px-3 py-2 font-normal">Status</th><th className="text-right px-3 py-2 font-normal">Action</th></tr>
                  </thead>
                  <tbody>
                    {data.payments.map((p) => (
                      <tr key={p.id} className="border-t border-white/5" data-testid={`sched-row-${p.id}`}>
                        <td className="px-3 py-2 text-white/50">{p.sequence}</td>
                        <td className="px-3 py-2 text-white/80">{p.due_date}</td>
                        <td className="px-3 py-2 text-white/80">{fmtCurrency(p.amount)}</td>
                        <td className="px-3 py-2">
                          <span className={`inline-block text-[10px] uppercase tracking-[0.16em] px-2 py-0.5 border ${STATUS_COLORS[p.status] || ""}`}>{p.status}</span>
                        </td>
                        <td className="px-3 py-2 text-right">
                          {p.status !== "paid" && (
                            <div className="inline-flex gap-2">
                              <button onClick={() => stripeLink(p.id)} data-testid={`sched-stripe-${p.id}`} className="text-[10px] uppercase tracking-[0.15em] text-[#D4AF37] hover:underline">Stripe link</button>
                              <button onClick={() => markPaid(p.id)} data-testid={`sched-markpaid-${p.id}`} className="text-[10px] uppercase tracking-[0.15em] text-emerald-400 hover:underline">Mark paid</button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="pt-2 flex justify-between items-center">
              <button
                onClick={sendInvite}
                disabled={inviting}
                data-testid="lease-invite-portal"
                className="text-xs uppercase tracking-[0.16em] text-[#D4AF37] hover:underline disabled:opacity-50"
              >
                {inviting ? "Sending…" : "Send portal invite →"}
              </button>
              <button onClick={terminate} data-testid="lease-terminate" className="text-xs uppercase tracking-[0.16em] text-red-400 hover:underline">
                Terminate lease
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== ANALYTICS TAB ====================

function AnalyticsTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/rto/analytics").then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return <div className="p-16 text-center text-white/50 text-sm">Loading…</div>;

  return (
    <div data-testid="analytics-tab" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <KpiCard icon={<Car size={16} />} label="Fleet size" value={data.fleet.total} sub={`Utilization ${data.fleet.utilization_pct}%`} testId="kpi-fleet" />
      <KpiCard icon={<FileText size={16} />} label="Active leases" value={data.leases.active} sub={`${Object.entries(data.leases.by_status).map(([k, v]) => `${v} ${k}`).join(" · ")}`} testId="kpi-leases" />
      <KpiCard icon={<DollarSign size={16} />} label="Revenue MTD" value={fmtCurrency(data.revenue.mtd)} sub={`YTD ${fmtCurrency(data.revenue.ytd)}`} testId="kpi-revenue" />
      <KpiCard icon={<AlertCircle size={16} />} label="Delinquent" value={data.delinquency.count} sub={fmtCurrency(data.delinquency.amount)} accent="red" testId="kpi-delinquency" />
      {data.top_vehicle && (
        <div className="md:col-span-2 lg:col-span-4 bg-[#0a0a0a] border border-[#D4AF37]/30 p-6" data-testid="top-vehicle-card">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp size={14} className="text-[#D4AF37]" />
            <span className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">Top earning vehicle</span>
          </div>
          <div className="text-white text-lg">{data.top_vehicle.label}</div>
          <div className="text-white/50 text-sm mt-1">{fmtCurrency(data.top_vehicle.revenue)} collected</div>
        </div>
      )}
      <div className="md:col-span-2 lg:col-span-4 bg-[#0a0a0a] border border-white/10 p-6" data-testid="fleet-status-breakdown">
        <div className="text-[10px] uppercase tracking-[0.22em] text-white/50 mb-3">Fleet by status</div>
        <div className="flex flex-wrap gap-3">
          {Object.entries(data.fleet.by_status).map(([k, v]) => (
            <div key={k} className={`px-4 py-2 border ${STATUS_COLORS[k] || ""}`}>
              <div className="text-2xl text-white">{v}</div>
              <div className="text-[10px] uppercase tracking-[0.16em] opacity-70">{k}</div>
            </div>
          ))}
          {Object.keys(data.fleet.by_status).length === 0 && (
            <div className="text-white/40 text-sm">No fleet yet — add vehicles on the Fleet tab.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function KpiCard({ icon, label, value, sub, accent, testId }) {
  const accentClass = accent === "red" ? "border-red-500/30" : "border-white/10";
  return (
    <div data-testid={testId} className={`bg-[#0a0a0a] border ${accentClass} p-5`}>
      <div className="flex items-center gap-2 text-white/50 text-[10px] uppercase tracking-[0.22em] mb-3">
        {icon} {label}
      </div>
      <div className="font-display text-3xl text-white mb-1" style={{ fontWeight: 400 }}>{value}</div>
      {sub && <div className="text-xs text-white/40">{sub}</div>}
    </div>
  );
}

// ==================== COMMON PRIMITIVES ====================

function Modal({ title, onClose, children, testId }) {
  return (
    <div data-testid={testId} className="fixed inset-0 z-40 flex items-start md:items-center justify-center p-4 overflow-y-auto">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-[#0a0a0a] border border-white/10 my-8">
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-3">
          <h3 className="font-display text-xl text-white" style={{ fontWeight: 300 }}>{title}</h3>
          <button onClick={onClose} className="text-white/50 hover:text-white p-1"><X size={16} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
function Row({ children }) { return <div className="grid grid-cols-2 gap-3">{children}</div>; }
function Field({ label, value, onChange, testId, type = "text", required, placeholder, step }) {
  return (
    <label className="block flex-1">
      <span className="text-[10px] uppercase tracking-[0.22em] text-white/50 mb-2 block">{label}</span>
      <input type={type} step={step} value={value} onChange={onChange} required={required} placeholder={placeholder} data-testid={testId}
        className="w-full bg-[#050505] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#D4AF37] focus:outline-none" />
    </label>
  );
}
function Info({ label, value, sub }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-white/40">{label}</div>
      <div className="text-white/90">{value || "—"}</div>
      {sub && <div className="text-xs text-white/40">{sub}</div>}
    </div>
  );
}
